import { LangToggle } from "@/pages/Legal";
import { ProW9Card } from "@/components/W9";
import { ProAgreementCard, ProReferrals } from "@/components/ProPartner";
import { LegalLinks } from "@/components/FundingDisclosure";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Sparkles } from "lucide-react";
import { useTour } from "@/components/tour/TourProvider";
import { PageHelpButton } from "@/components/tour/PageHelpButton";
import { useSpanishScreens } from "@/lib/i18n";
import { Brand } from "@/components/Brand";
import { StatusBadge, TurnaroundBadge } from "@/components/Badges";
import { ProEarnings } from "@/components/Payouts";
import { ReviewDialog } from "@/pages/Admin";
import { ConsultRow } from "@/components/ConsultQueue";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { REVIEW_STATUS } from "@/lib/constants";

export default function ProPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const { start: startTour, offerTour, lang } = useTour();
  useSpanishScreens(lang === "es");
  useEffect(() => { api.get("/pro/assignments").then((r) => setD(r.data)); }, []);
  useEffect(() => { if (d && user?.role === "pro") offerTour("pro"); }, [d, user, offerTour]);
  const out = async () => { await logout(); navigate("/"); };
  const replace = (key, list, n) => setD((x) => ({ ...x, [list]: x[list].map((i) => (i[key] === n[key] ? n : i)) }));
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200"><div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
        <Brand to="/pro" />
        <div className="flex items-center gap-2 sm:gap-4 text-sm shrink-0"><LangToggle /><button onClick={() => startTour("pro")} className="text-emerald-700 font-semibold flex items-center gap-1 hover:text-emerald-800" data-testid="pro-tour-button"><Sparkles className="h-4 w-4" />Tour</button><span className="hidden sm:inline text-slate-600" data-testid="pro-portal-user">{user?.name}</span><button onClick={out} className="text-slate-500 hover:text-red-600 flex items-center justify-center gap-1 min-h-[44px] min-w-[44px] px-2" aria-label="Sign out" data-testid="pro-logout-button"><LogOut className="h-4 w-4" /><span className="hidden sm:inline">Sign out</span></button></div>
      </div></header>
      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="eyebrow mb-2">{d?.type_label || "Professional"} workspace</div>
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900">My assignments</h1>
        {!d ? <Loader2 className="h-6 w-6 animate-spin text-emerald-600 mt-8" /> : (
          <div className="mt-8 space-y-8">
            <ProW9Card />
            <ProEarnings />
            <ProAgreementCard />
            <ProReferrals />
            {d.pro_type === "editor" ? (
              <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="pro-reviews-list">
                {d.reviews.length === 0 && <p className="p-6 text-sm text-slate-500" data-testid="pro-empty">No proposals assigned to you yet.</p>}
                {d.reviews.map((r) => (
                  <button key={r.review_id} onClick={() => setOpen(r)} className="w-full text-left p-5 flex items-center gap-4 hover:bg-slate-50" data-testid={`pro-review-row-${r.review_id}`}>
                    <div className="flex-1 min-w-0"><div className="font-semibold text-slate-900 truncate">{r.proposal_title}</div><div className="text-xs text-slate-500">Client {r.user_email} · {new Date(r.submitted_at || r.created_at).toLocaleDateString(lang === "es" ? "es" : "en-US")}</div></div>
                    <TurnaroundBadge r={r} testid={`pro-review-turnaround-${r.review_id}`} />
                    <StatusBadge status={r.status} map={REVIEW_STATUS} />
                  </button>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="pro-consults-list">
                {d.consultations.length === 0 && <p className="p-6 text-sm text-slate-500" data-testid="pro-empty">No requests assigned to you yet.</p>}
                {d.consultations.map((c) => <ConsultRow key={c.consult_id} c={c} base="/pro/consultations" onSaved={(n) => replace("consult_id", "consultations", n)} />)}
              </div>
            )}
          </div>
        )}
      </main>
      <footer className="max-w-5xl mx-auto px-6 pb-10" data-testid="pro-legal-footer"><LegalLinks partner={false} className="border-t border-slate-200 pt-5" /></footer>
      <PageHelpButton sidebar={false} />
      {open && <ReviewDialog review={open} base="/pro/reviews" onClose={() => setOpen(null)} onSaved={(n) => { replace("review_id", "reviews", n); setOpen(null); }} />}
    </div>
  );
}
